// F-0766 (`docs/concept-etrun-run-visibility.md` §4, D-0458 b): the one documented address that opens a
// repository file at a commit and a line, in its two forms. The direct form is the file page's own
// address with `at` and `line`; the lookup form names the git remote instead of the work area. Both
// are read by `parseRepositoryFileOpenSearch` and written by the builders below, so the page, the
// server and the docs example cannot disagree. Kept apart from `contract.ts`: the web's lookup page
// and the file page read it, and neither needs the machine wire's types.

import { appPath, workAreaRoutePath } from '../addressing/paths.js';
import { normalizeRepositoryRemote } from '../repository-identity.js';
import { REPOSITORY_PATH_MAX_LENGTH } from '../repository-paths.js';
import { REPOSITORY_FILE_COMMIT_PATTERN } from './routes.js';

/** The lookup page's address, `/-/open/file` (§4.2). */
export const REPOSITORY_FILE_OPEN_PATH = appPath('/open/file');

/** The same lookup as an API call, under `/api` (§5.4). */
export const REPOSITORY_FILE_OPEN_API_PATH = '/repository-files/open';

/** A line number has at most this many digits: ten million lines is no source file. */
const LINE_MAX_DIGITS = 7;
const LINE_PATTERN = new RegExp(
  `^([1-9][0-9]{0,${LINE_MAX_DIGITS - 1}})(?:-([1-9][0-9]{0,${LINE_MAX_DIGITS - 1}}))?$`,
);

/** The lines an address names: one line is a range of one. Lines count from 1. */
export interface IRepositoryFileLineRange {
  start: number;
  end: number;
}

/** Where in a file an address opens: the commit and the lines, each optional. */
export interface IRepositoryFilePlace {
  /** A full commit id, lower-case, or null for the default branch (§12.10). */
  at: string | null;
  line: IRepositoryFileLineRange | null;
}

/** What a lookup address asks for: the repository by its identity, the path and the place. */
export interface IRepositoryFileOpenRequest extends IRepositoryFilePlace {
  /** The repository's identity, `gitlab.com/evoye/x` (D-0010). */
  repository: string;
  path: string;
}

/**
 * Why a parameter is refused (§4.3). Each names its parameter by its first word, so the sentence a
 * refusal shows and the `parameter` it reports never disagree.
 */
export const REPOSITORY_FILE_OPEN_PROBLEMS = [
  'remote-missing',
  'remote-unknown',
  'path-missing',
  'path-outside',
  'path-too-long',
  'at-malformed',
  'line-malformed',
] as const;
export type RepositoryFileOpenProblemType = (typeof REPOSITORY_FILE_OPEN_PROBLEMS)[number];

/** The address's parameters, in the order the builders write them. */
export const REPOSITORY_FILE_OPEN_PARAMETERS = ['remote', 'path', 'at', 'line'] as const;
export type RepositoryFileOpenParameterType = (typeof REPOSITORY_FILE_OPEN_PARAMETERS)[number];

export function repositoryFileOpenProblemParameter(
  problem: RepositoryFileOpenProblemType,
): RepositoryFileOpenParameterType {
  return problem.slice(0, problem.indexOf('-')) as RepositoryFileOpenParameterType;
}

export type RepositoryFileOpenParseType =
  | { ok: true; request: IRepositoryFileOpenRequest }
  | {
      ok: false;
      problem: RepositoryFileOpenProblemType;
      parameter: RepositoryFileOpenParameterType;
    };

/** One work area the lookup found, with the direct address of the file in it (§5.4). */
export interface IRepositoryFileOpenWorkArea {
  id: string;
  name: string;
  /** The work area's key-form route reference, `<namespace key>/code` (D-0076, AC-K12). */
  reference: string;
  /** §4.1's address in this work area, relative to the app host. */
  address: string;
}

/** `GET /api/repository-files/open`: the repository read, and the reader's work areas holding it. */
export interface IRepositoryFileOpenAnswer {
  repository: string;
  workAreas: IRepositoryFileOpenWorkArea[];
}

/** A malformed lookup's `400` body. */
export interface IRepositoryFileOpenRefusal {
  reason: 'malformed';
  parameter: RepositoryFileOpenParameterType;
  problem: RepositoryFileOpenProblemType;
  message: string;
}

/** `42` or `42-48` as a range; null for anything else, a range that runs backwards among them. */
export function parseRepositoryFileLine(
  value: string | null | undefined,
): IRepositoryFileLineRange | null {
  const match = LINE_PATTERN.exec((value ?? '').trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = match[2] === undefined ? start : Number(match[2]);
  return end >= start ? { start, end } : null;
}

export function formatRepositoryFileLine(line: IRepositoryFileLineRange): string {
  return line.end === line.start ? String(line.start) : `${line.start}-${line.end}`;
}

/**
 * A remote as `git remote get-url origin` prints it, or the bare identity `host/path`, as the
 * repository's identity (D-0010); null when it names no repository FEATit can match.
 */
export function repositoryOpenRemoteIdentity(remote: string | null | undefined): string | null {
  const value = (remote ?? '').trim();
  if (!value) return null;
  // The identity itself has no scheme and no `:`, and starts with a host: read it as https's.
  if (/^[a-z0-9-]+(?:\.[a-z0-9-]+)+\/[^:]+$/i.test(value)) {
    return normalizeRepositoryRemote(`https://${value}`);
  }
  return normalizeRepositoryRemote(value);
}

/** Why a path cannot name a file inside the repository, or null when it can (§4.3). */
export function repositoryOpenPathProblem(
  path: string | null | undefined,
): Extract<RepositoryFileOpenProblemType, `path-${string}`> | null {
  if (!path) return 'path-missing';
  if (path.length > REPOSITORY_PATH_MAX_LENGTH) return 'path-too-long';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return 'path-outside';
  if (/^[/~]/.test(path) || /^[a-z]:/i.test(path)) return 'path-outside';
  const segments = path.split('/');
  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
    return 'path-outside';
  }
  return null;
}

/** A commit an address may name: a full id, read lower-case; null when absent, undefined when malformed. */
function commitOf(value: string | null): string | null | undefined {
  if (value === null || value === '') return null;
  const commit = value.trim().toLowerCase();
  return REPOSITORY_FILE_COMMIT_PATTERN.test(commit) ? commit : undefined;
}

function refused(problem: RepositoryFileOpenProblemType): RepositoryFileOpenParseType {
  return { ok: false, problem, parameter: repositoryFileOpenProblemParameter(problem) };
}

/**
 * The lookup address's query (§4.2), every parameter checked by §4.3's rules. The first problem in
 * parameter order is the one reported.
 */
export function parseRepositoryFileOpenSearch(
  search: string | URLSearchParams,
): RepositoryFileOpenParseType {
  const query = typeof search === 'string' ? new URLSearchParams(search) : search;
  const remote = query.get('remote');
  if (!remote?.trim()) return refused('remote-missing');
  const repository = repositoryOpenRemoteIdentity(remote);
  if (!repository) return refused('remote-unknown');
  const path = query.get('path');
  const pathProblem = repositoryOpenPathProblem(path);
  if (pathProblem) return refused(pathProblem);
  const place = readPlace(query);
  if (!place.ok) return refused(place.problem);
  return { ok: true, request: { repository, path: path!, at: place.at, line: place.line } };
}

/**
 * §4.1's direct address's `at` and `line`, each checked by §4.3's rules as the lookup checks them; a
 * malformed one is refused, unlike {@link readRepositoryFilePlace}, which the page reads leniently.
 */
export function parseRepositoryFilePlaceSearch(
  search: string | URLSearchParams,
):
  | ({ ok: true } & IRepositoryFilePlace)
  | { ok: false; problem: 'at-malformed' | 'line-malformed' } {
  return readPlace(typeof search === 'string' ? new URLSearchParams(search) : search);
}

function readPlace(
  query: URLSearchParams,
):
  | ({ ok: true } & IRepositoryFilePlace)
  | { ok: false; problem: 'at-malformed' | 'line-malformed' } {
  const at = commitOf(query.get('at'));
  if (at === undefined) return { ok: false, problem: 'at-malformed' };
  const lineValue = query.get('line');
  const line = lineValue === null || lineValue === '' ? null : parseRepositoryFileLine(lineValue);
  if (lineValue !== null && lineValue !== '' && !line)
    return { ok: false, problem: 'line-malformed' };
  return { ok: true, at, line };
}

/**
 * The file page's own reading of its address (§5.5): the commit and the lines, each null when
 * absent or malformed. The page opens what it can; only the lookup refuses a malformed parameter.
 */
export function readRepositoryFilePlace(search: string | URLSearchParams): IRepositoryFilePlace {
  const query = typeof search === 'string' ? new URLSearchParams(search) : search;
  return {
    at: commitOf(query.get('at')) ?? null,
    line: parseRepositoryFileLine(query.get('line')),
  };
}

/** `?at=…&line=…`, or `''` when neither is named: the query §4.1's address carries. */
export function repositoryFilePlaceSearch(place: Partial<IRepositoryFilePlace>): string {
  const query = new URLSearchParams();
  if (place.at) query.set('at', place.at.toLowerCase());
  if (place.line) query.set('line', formatRepositoryFileLine(place.line));
  const text = query.toString();
  return text ? `?${text}` : '';
}

/**
 * §4.2's lookup address, relative to the app host: `/-/open/file?remote=…&path=…&at=…&line=…`.
 * `remote` is written as given, so a tool's own spelling of it survives into the address.
 */
export function repositoryFileOpenAddress(input: {
  remote: string;
  path: string;
  at?: string | null;
  line?: IRepositoryFileLineRange | null;
}): string {
  const query = new URLSearchParams({ remote: input.remote, path: input.path });
  if (input.at) query.set('at', input.at.toLowerCase());
  if (input.line) query.set('line', formatRepositoryFileLine(input.line));
  return `${REPOSITORY_FILE_OPEN_PATH}?${query.toString()}`;
}

/**
 * §4.1's direct address in one work area, relative to the app host:
 * `/<namespace>/<work area>/-/files/<path>?at=…&line=…`. Each segment of the path is encoded and the
 * slashes kept, as the web's `repositoryFilePath` writes it.
 */
export function repositoryFileDirectAddress(
  workAreaReference: string,
  path: string,
  place: Partial<IRepositoryFilePlace> = {},
): string {
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  return `${workAreaRoutePath(workAreaReference, `/files/${encoded}`)}${repositoryFilePlaceSearch(place)}`;
}
